import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const longVowelExamples = [
  // Ba
  { letter: "با", pronunciation: "Baa - Ba with Alif" },
  { letter: "بو", pronunciation: "Boo - Ba with Waw" },
  { letter: "بي", pronunciation: "Bee - Ba with Ya" },

  // Ta
  { letter: "تا", pronunciation: "Taa - Ta with Alif" },
  { letter: "تو", pronunciation: "Too - Ta with Waw" },
  { letter: "تي", pronunciation: "Tee - Ta with Ya" },

  // Jeem
  { letter: "جا", pronunciation: "Jaa - Jeem with Alif" },
  { letter: "جو", pronunciation: "Joo - Jeem with Waw" },
  { letter: "جي", pronunciation: "Jee - Jeem with Ya" },

  // Seen
  { letter: "سا", pronunciation: "Saa - Seen with Alif" },
  { letter: "سو", pronunciation: "Soo - Seen with Waw" },
  { letter: "سي", pronunciation: "See - Seen with Ya" },

  // Kaf
  { letter: "كا", pronunciation: "Kaa - Kaf with Alif" },
  { letter: "كو", pronunciation: "Koo - Kaf with Waw" },
  { letter: "كي", pronunciation: "Kee - Kaf with Ya" },

  // Meem
  { letter: "ما", pronunciation: "Maa - Meem with Alif" },
  { letter: "مو", pronunciation: "Moo - Meem with Waw" },
  { letter: "مي", pronunciation: "Mee - Meem with Ya" },

  // Lam
  { letter: "لا", pronunciation: "Laa - Lam with Alif" },
  { letter: "لو", pronunciation: "Loo - Lam with Waw" },
  { letter: "لي", pronunciation: "Lee - Lam with Ya" },

  // Noon
  { letter: "نا", pronunciation: "Naa - Noon with Alif" },
  { letter: "نو", pronunciation: "Noo - Noon with Waw" },
  { letter: "ني", pronunciation: "Nee - Noon with Ya" },

  // Dal
  { letter: "دا", pronunciation: "Daa - Dal with Alif" },
  { letter: "دو", pronunciation: "Doo - Dal with Waw" },
  { letter: "دي", pronunciation: "Dee - Dal with Ya" },

  // Ra
  { letter: "را", pronunciation: "Raa - Ra with Alif" },
  { letter: "رو", pronunciation: "Roo - Ra with Waw" },
  { letter: "ري", pronunciation: "Ree - Ra with Ya" },

  // Ha
  { letter: "ها", pronunciation: "Haa - Ha with Alif" },
  { letter: "هو", pronunciation: "Hoo - Ha with Waw" },
  { letter: "هي", pronunciation: "Hee - Ha with Ya" },

  // Fa
  { letter: "فا", pronunciation: "Faa - Fa with Alif" },
  { letter: "فو", pronunciation: "Foo - Fa with Waw" },
  { letter: "في", pronunciation: "Fee - Fa with Ya" },

  // Qaf
  { letter: "قا", pronunciation: "Qaa - Qaf with Alif" },
  { letter: "قو", pronunciation: "Qoo - Qaf with Waw" },
  { letter: "قي", pronunciation: "Qee - Qaf with Ya" },

  // Ayn
  { letter: "عا", pronunciation: "Aaa - Ayn with Alif" },
  { letter: "عو", pronunciation: "Aoo - Ayn with Waw" },
  { letter: "عي", pronunciation: "Aee - Ayn with Ya" },
];

function LongVowelsLesson() {
  const [items, setItems] = useState(longVowelExamples);
  const [clicked, setClicked] = useState(new Set());
  const [pronunciation, setPronunciation] = useState("");

  const handleClick = (letter, sound) => {
    setPronunciation(sound);
    setClicked((prev) => new Set(prev).add(letter));
    window.responsiveVoice.speak(letter, "Arabic Male");
  };

  const reset = () => {
    setItems(longVowelExamples);
    setClicked(new Set());
    setPronunciation("");
  };

  const shuffle = () => {
    const shuffled = [...items].sort(() => Math.random() - 0.5);
    setItems(shuffled);
    setPronunciation("");
  };

  const progress = (clicked.size / items.length) * 100;

  return (
    <div dir="rtl" className="p-4 sm:p-6 text-center font-arabic min-h-screen">
      <h2 className="text-3xl sm:text-4xl font-bold mb-6 text-purple-800">الحركات الطويلة</h2>

      {/* Progress Bar */}
      <div className="w-full max-w-md mx-auto bg-gray-200 rounded-full h-4 mb-4 overflow-hidden shadow-inner">
        <motion.div
          className="bg-purple-500 h-full"
          initial={{ width: 0 }}
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.5 }}
        />
      </div>
      <p className="text-sm text-gray-600 mb-6">
        تم تعلم {clicked.size} من {items.length} حرفًا
      </p>

      {/* Pronunciation Display */}
      <AnimatePresence>
        {pronunciation && (
          <motion.div
            key="pronunciation"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.3 }}
            className="text-xl sm:text-2xl font-semibold text-purple-700 mb-6"
          >
            النطق: {pronunciation}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Letter Grid */}
      <div className="grid grid-cols-3 sm:grid-cols-5 gap-4 justify-center mb-6">
        {items.map(({ letter, pronunciation }, idx) => {
          const isClicked = clicked.has(letter);
          return (
            <button
              key={idx}
              onClick={() => handleClick(letter, pronunciation)}
              className={`text-3xl sm:text-4xl font-bold p-5 rounded-2xl shadow-lg transition duration-200 ${
                isClicked
                  ? "bg-purple-100 text-purple-700"
                  : "bg-white text-purple-800 hover:bg-purple-200"
              }`}
              title={pronunciation}
            >
              {letter}
            </button>
          );
        })}
      </div>

      {/* Controls */}
      <div className="flex flex-col sm:flex-row gap-4 items-center justify-center">
        <button
          onClick={reset}
          className="bg-green-500 hover:bg-green-600 text-white text-lg px-6 py-3 rounded-full shadow-lg transition duration-200 w-64 sm:w-auto"
        >
          إعادة الترتيب - Reset
        </button>
        <button
          onClick={shuffle}
          className="bg-yellow-500 hover:bg-yellow-600 text-white text-lg px-6 py-3 rounded-full shadow-lg transition duration-200 w-64 sm:w-auto"
        >
          خلط الحروف - Shuffle
        </button>
      </div>

      {/* Voice Library */}
      <script src="https://code.responsivevoice.org/responsivevoice.js?key=1X64heoV"></script>
    </div>
  );
}

export default LongVowelsLesson;
